import { useEffect, useState } from 'react';

/**
 * useCoarsePointer — reports whether the primary input device is coarse
 * (touch screen on a phone or tablet) vs. fine (mouse / trackpad).
 *
 * Wraps `window.matchMedia('(pointer: coarse)')` with a stable subscription
 * so consumers re-render when the user docks a keyboard, plugs in a mouse,
 * etc.  The initial value is read synchronously where possible so the very
 * first render already reflects the device — avoiding a one-frame flash on
 * the desktop branch.
 *
 * Used by ProcedureWidget to branch the chip-tap into a dental-chairside
 * full-bleed chart on iPad / Android tablets / phones, while leaving the
 * desktop rail-expand behaviour untouched.
 */
export default function useCoarsePointer() {
  const [isCoarse, setIsCoarse] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    try { return window.matchMedia('(pointer: coarse)').matches; }
    catch (_e) { return false; }
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    let mq;
    try { mq = window.matchMedia('(pointer: coarse)'); }
    catch (_e) { return undefined; }

    const onChange = (e) => setIsCoarse(!!e.matches);
    setIsCoarse(mq.matches);

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    // Safari < 14 / legacy fallback.
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  return isCoarse;
}
